/**
 * Forward migration of saved maps. Every shipped schema version stays readable: a v2 file saved
 * before colours existed must still open, and must look the same after it does, so the defaults
 * chosen here are exactly the constants the v2 renderer hard-coded. Stone and vegetation props
 * (v2–v7) are dropped: those tools never shipped as a finished look.
 */

import { clampDepth, clampElevation, mapLapCount } from './document.ts';
import { migrateMapMeta } from './meta.ts';
import { sanitizeMapName } from './name.ts';
import { createDefaultEnvironment, type Environment } from './environment.ts';
import {
  lookColorFromUnknown,
  lookFromEnvironment,
  lookWithV13Extras,
  type MapLook,
} from './look.ts';
import { sunSettingsFromTimeOfDay } from './sun.ts';
import {
  DEFAULT_LAND_COLOR,
  DEFAULT_REEF_COLOR,
  DEFAULT_ROCK_COLOR,
  DEFAULT_PARK_AREA_COLOR,
  DEFAULT_START_AREA_COLOR,
  colorFromUnknown,
} from './palette.ts';
import { clampSkirtM, emptyIslandLook, isPropId } from './surfaces.ts';
import { defaultColorForMaterial } from './shallowWater.ts';
import {
  DEFAULT_MAP_HEIGHT,
  DEFAULT_MAP_WIDTH,
  DEFAULT_ROCK_ELEVATION,
  DEFAULT_WATER_DEPTH,
  MAX_CORNER_RADIUS,
  MIN_LAPS,
  MIN_ROUTE_VERTICES,
  type IslandDecoration,
  type MapDocument,
  type MapRoute,
  type Rock,
  type ShallowWaterZone,
  type Vec2,
  type WaterMaterial,
} from './types.ts';
import { ROUTE_COLORS } from './routes.ts';
import { absoluteElevationsToRelative } from './elevationStack.ts';
import { CURRENT_MAP_VERSION, OLDEST_SUPPORTED_MAP_VERSION } from './version.ts';

export { CURRENT_MAP_VERSION, OLDEST_SUPPORTED_MAP_VERSION };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

function asPoint(value: unknown): Vec2 {
  if (!isRecord(value)) {
    return { x: 0, y: 0 };
  }

  return { x: asNumber(value.x, 0), y: asNumber(value.y, 0) };
}

function asRing(value: Record<string, unknown>): {
  vertices: Vec2[];
  edges: { bend: Vec2 | null }[];
  cornerRadius: number;
} {
  const vertices = asArray(value.vertices).map(asPoint);
  const rawEdges = asArray(value.edges);
  const edges = vertices.map((_, index) => {
    const edge = rawEdges[index];
    const bend = isRecord(edge) ? edge.bend : null;
    return { bend: isRecord(bend) ? asPoint(bend) : null };
  });

  return {
    vertices,
    edges,
    // Files written before this field existed used the old hard-coded 50.
    cornerRadius: asNumber(value.cornerRadius, 50),
  };
}

const WATER_MATERIALS: readonly WaterMaterial[] = ['sand', 'stone', 'reef'];

function asMaterial(value: unknown): WaterMaterial {
  return WATER_MATERIALS.find((material) => material === value) ?? 'sand';
}

/** Pre-v9 islands were a flat swatch. v10 drops surface recipes and ice/boulder props. */
function migrateIslandLook(raw: Record<string, unknown>): ReturnType<typeof emptyIslandLook> {
  const fallback = emptyIslandLook();
  const decorations: IslandDecoration[] = [];

  for (const entry of asArray(raw.decorations)) {
    if (!isRecord(entry) || !isPropId(entry.catalog)) {
      continue;
    }

    decorations.push({
      id: asString(entry.id, `deco-${decorations.length + 1}`),
      catalog: entry.catalog,
      x: asNumber(entry.x, 0),
      y: asNumber(entry.y, 0),
      scale: asNumber(entry.scale, 1),
      rotation: asNumber(entry.rotation, 0),
    });
  }

  return {
    skirtM: clampSkirtM(asNumber(raw.skirtM, fallback.skirtM)),
    decorations,
    nextDecorationId: Math.max(
      asNumber(raw.nextDecorationId, decorations.length + 1),
      decorations.length + 1,
    ),
  };
}

function migrateRock(value: unknown, index: number): Rock {
  const raw = isRecord(value) ? value : {};
  return {
    id: asString(raw.id, `rock-${index + 1}`),
    color: colorFromUnknown(raw.color, DEFAULT_ROCK_COLOR),
    elevation: clampElevation(asNumber(raw.elevation, DEFAULT_ROCK_ELEVATION)),
    ...asRing(raw),
  };
}

function migrateReefAsZone(value: unknown, index: number): ShallowWaterZone {
  const raw = isRecord(value) ? value : {};
  return {
    id: asString(raw.id, `reef-${index + 1}`),
    material: 'reef',
    color: colorFromUnknown(raw.color, DEFAULT_REEF_COLOR),
    depth: clampDepth(asNumber(raw.depth, DEFAULT_WATER_DEPTH)),
    ...asRing(raw),
  };
}

function migrateRoute(value: unknown, index: number): MapRoute | null {
  const raw = isRecord(value) ? value : {};
  const points = asArray(raw.points).map(asPoint);

  if (points.length < MIN_ROUTE_VERTICES) {
    return null;
  }

  const fallback = ROUTE_COLORS[index % ROUTE_COLORS.length] ?? ROUTE_COLORS[0]!;

  return {
    id: asString(raw.id, `route-${index + 1}`),
    color: colorFromUnknown(raw.color, fallback),
    points,
    cornerRadius: asNumber(raw.cornerRadius, MAX_CORNER_RADIUS),
  };
}

function migrateShallowZone(value: unknown, index: number): ShallowWaterZone {
  const raw = isRecord(value) ? value : {};
  const material = asMaterial(raw.material);
  return {
    id: asString(raw.id, `water-${index + 1}`),
    material,
    color: colorFromUnknown(raw.color, defaultColorForMaterial(material)),
    depth: clampDepth(asNumber(raw.depth, 0)),
    ...asRing(raw),
  };
}

function idNumericSuffix(id: string): number {
  const match = /-(\d+)$/.exec(id);
  return match ? Number(match[1]) : 0;
}

function migrateEnvironment(value: unknown): Environment {
  const defaults = createDefaultEnvironment();

  if (!isRecord(value)) {
    return defaults;
  }

  const wind = isRecord(value.wind) ? value.wind : {};

  const timeOfDay = asNumber(value.timeOfDay, defaults.timeOfDay);
  const derivedSun = sunSettingsFromTimeOfDay(timeOfDay);
  const rawSun = isRecord(value.sun) ? value.sun : null;

  // Pre-v16 files had no enable flags. A non-noon map keeps looking the same (Time on, sun kept),
  // and every old map stays calm (Wind off) even if a past Randomize left a speed in the file.
  const timeEnabled =
    typeof value.timeEnabled === 'boolean'
      ? value.timeEnabled
      : timeOfDay !== defaults.timeOfDay;
  const windEnabled = typeof wind.enabled === 'boolean' ? wind.enabled : false;

  return {
    seed: asNumber(value.seed, defaults.seed),
    timeEnabled,
    timeOfDay,
    sun: {
      azimuth: asNumber(rawSun?.azimuth, derivedSun.azimuth),
      altitude: asNumber(rawSun?.altitude, derivedSun.altitude),
      strength: asNumber(rawSun?.strength, derivedSun.strength),
    },
    wind: {
      enabled: windEnabled,
      directionDeg: asNumber(wind.directionDeg, defaults.wind.directionDeg),
      speed: asNumber(wind.speed, defaults.wind.speed),
    },
  };
}

/**
 * Pre-v5 maps derived the sheet from Time. Stamp that derived look so they paint the same after
 * the bump; an authored v5 `look` is kept as written.
 */
function migrateLook(value: unknown, environment: Environment): MapLook {
  const derived = lookFromEnvironment(environment);

  if (!isRecord(value)) {
    return lookWithV13Extras(derived, null);
  }

  return lookWithV13Extras(
    {
      waterColor: lookColorFromUnknown(value.waterColor, value.waterAlpha, derived.waterColor),
      gridColor: lookColorFromUnknown(value.gridColor, value.gridAlpha, derived.gridColor),
    },
    value,
  );
}

/**
 * Normalises any supported version into a current `MapDocument`, or returns null if the input is
 * not a map at all. Unknown fields are dropped; missing fields take the previous renderer's
 * implied defaults.
 */
export function migrateMapDocument(value: unknown): MapDocument | null {
  if (!isRecord(value)) {
    return null;
  }

  const version = value.version;

  if (
    typeof version !== 'number' ||
    version < OLDEST_SUPPORTED_MAP_VERSION ||
    version > CURRENT_MAP_VERSION
  ) {
    return null;
  }

  if (!isRecord(value.bounds) || !Array.isArray(value.islands)) {
    return null;
  }

  const islands = asArray(value.islands).map((raw, index) => {
    const island = isRecord(raw) ? raw : {};
    const look = migrateIslandLook(island);
    return {
      id: asString(island.id, `island-${index + 1}`),
      color: colorFromUnknown(island.color, DEFAULT_LAND_COLOR),
      // Pre-v4 islands were flat, so a missing elevation is 0 and an old map stays flat.
      elevation: clampElevation(asNumber(island.elevation, 0)),
      ...look,
      ...asRing(island),
    };
  });

  const rocks = asArray(value.rocks).map(migrateRock);

  // v10 and older stored metres above water. Nested bodies become relative.
  if (version <= 10) {
    absoluteElevationsToRelative([...islands, ...rocks]);
  }

  const fromReefs = asArray(value.reefs).map(migrateReefAsZone);
  const fromShallows = asArray(value.shallowWater).map(migrateShallowZone);
  const shallowWater = [...fromReefs, ...fromShallows];
  const nextFromIds =
    shallowWater.reduce((max, zone) => Math.max(max, idNumericSuffix(zone.id)), 0) + 1;

  const startAreaRaw = value.startArea;
  const startArea = isRecord(startAreaRaw)
    ? {
        id: 'start-area',
        color: colorFromUnknown(startAreaRaw.color, DEFAULT_START_AREA_COLOR),
        ...asRing(startAreaRaw),
      }
    : null;

  // v18: optional park ring for post-finish autopilot. Missing → null.
  const parkAreaRaw = value.parkArea;
  const parkArea = isRecord(parkAreaRaw)
    ? {
        id: 'park-area',
        color: colorFromUnknown(parkAreaRaw.color, DEFAULT_PARK_AREA_COLOR),
        ...asRing(parkAreaRaw),
      }
    : null;

  const checkpoints = asArray(value.checkpoints).map((raw, index) => {
    const checkpoint = isRecord(raw) ? raw : {};
    return {
      id: asString(checkpoint.id, `checkpoint-${index + 1}`),
      a: asPoint(checkpoint.a),
      b: asPoint(checkpoint.b),
      order:
        typeof checkpoint.order === 'number' && checkpoint.order > 0 ? checkpoint.order : null,
      isGoal: checkpoint.isGoal === true,
    };
  });

  const environment = migrateEnvironment(value.environment);
  const routes = asArray(value.routes)
    .map(migrateRoute)
    .filter((route): route is MapRoute => route !== null);
  const nextRouteFromIds =
    routes.reduce((max, route) => Math.max(max, idNumericSuffix(route.id)), 0) + 1;

  return {
    version: CURRENT_MAP_VERSION,
    name: typeof value.name === 'string' ? sanitizeMapName(value.name) : '',
    meta: migrateMapMeta(value.meta),
    bounds: {
      width: Math.max(1, asNumber(value.bounds.width, DEFAULT_MAP_WIDTH)),
      height: Math.max(1, asNumber(value.bounds.height, DEFAULT_MAP_HEIGHT)),
    },
    environment,
    look: migrateLook(value.look, environment),
    islands,
    rocks,
    shallowWater,
    checkpoints,
    laps: mapLapCount(asNumber(value.laps, MIN_LAPS)),
    routes,
    startArea,
    parkArea,
    nextIslandId: asNumber(value.nextIslandId, islands.length + 1),
    nextRockId: asNumber(value.nextRockId, rocks.length + 1),
    nextShallowWaterId: Math.max(
      asNumber(value.nextShallowWaterId, fromShallows.length + 1),
      asNumber(value.nextReefId, fromReefs.length + 1),
      nextFromIds,
    ),
    nextCheckpointId: asNumber(value.nextCheckpointId, checkpoints.length + 1),
    nextRouteId: Math.max(asNumber(value.nextRouteId, routes.length + 1), nextRouteFromIds),
  };
}
